/**
 * Phase 15 — Reusable empty state.
 *
 * Used by every list page / linked section that previously showed a bare
 * "No data" sentence. Keeps the look consistent and gives ops one obvious
 * next step when there is, in fact, nothing to look at.
 *
 * Usage:
 *   <EmptyState
 *     icon={UsersIcon}
 *     title="No customers yet"
 *     description="Add your first customer to start tracking jobs."
 *     cta={{ label: "Add Customer", onClick: open, testId: "..." }}
 *   />
 *
 *   // Inside a table:
 *   <EmptyState inTable colSpan={6} icon={...} title={...} description={...} />
 */
export default function EmptyState({
  icon: Icon,
  title,
  description,
  cta,
  inTable = false,
  colSpan = 6,
  testId,
}) {
  const body = (
    <div
      data-testid={testId}
      className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center"
    >
      {Icon && (
        <Icon
          size={36}
          weight="duotone"
          className="text-slate-300"
          aria-hidden="true"
        />
      )}
      <div>
        <div className="font-heading text-base font-bold text-slate-900">
          {title}
        </div>
        {description && (
          <div className="mt-1 max-w-md text-sm text-slate-500">
            {description}
          </div>
        )}
      </div>
      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          data-testid={cta.testId}
          className="mt-1 inline-flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
        >
          {cta.label}
        </button>
      )}
    </div>
  );

  if (inTable) {
    return (
      <tr>
        <td colSpan={colSpan} className="bg-white">
          {body}
        </td>
      </tr>
    );
  }
  return body;
}
